import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import TablePagination from "../TablePagination";
import OrderApi from "../../Api/OrderApi";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { formatDate, formatDateTime } from "../../helper/DateHelper";
import { Modal, Button } from "react-bootstrap";
import { IMAGE_BASE_URL } from "../../Config/Index";
import StandardDatePicker from "../StandardDatePicker";

const numberToWords = (num) => {
    if (!num || isNaN(num)) return "Zero";
    if (num === 0) return "Zero";
    const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const g = ['', 'Thousand', 'Million', 'Billion', 'Trillion', 'Quadrillion'];

    const makeGrp = (n) => {
        let st = '';
        if (n >= 100) {
            st += a[Math.floor(n / 100)] + ' Hundred ';
            n %= 100;
        }
        if (n >= 20) {
            st += b[Math.floor(n / 10)] + ' ';
            n %= 10;
        }
        if (n > 0) {
            st += a[n] + ' ';
        }
        return st;
    };

    let str = '';
    let i = 0;
    let integerPart = Math.floor(num);
    let decimalPart = Math.round((num - integerPart) * 100);

    while (integerPart > 0) {
        if (integerPart % 1000 !== 0) {
            str = makeGrp(integerPart % 1000) + g[i] + ' ' + str;
        }
        integerPart = Math.floor(integerPart / 1000);
        i++;
    }

    let result = "INR " + str.trim();
    if (decimalPart > 0) {
        result += " and " + makeGrp(decimalPart).trim() + " Paise";
    }

    return result;
};

const loadScript = (src) => {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.body.appendChild(script);
    });
};

const OrderListCommonLayer = ({ categoryStatus, categoryPayStatus, pageTitle }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [paymentFilter, setPaymentFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [counts, setCounts] = useState({});
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const showCheckbox = categoryStatus === "";
    const [refreshingOrderId, setRefreshingOrderId] = useState(null);
    const [isBatchVerifying, setIsBatchVerifying] = useState(false);
    const [isConfirmingBatch, setIsConfirmingBatch] = useState(false);
    const [showBatchResultModal, setShowBatchResultModal] = useState(false);
    const [batchResultData, setBatchResultData] = useState(null);
    const [selectedBatchOrderIds, setSelectedBatchOrderIds] = useState([]);
    const [printQueue, setPrintQueue] = useState([]);
    const [currentPrintIndex, setCurrentPrintIndex] = useState(-1);
    const isPrintingCancelledRef = useRef(false);

    const handleVerifyPaymentStatus = async (orderId) => {
        setRefreshingOrderId(orderId);
        try {
            const res = await OrderApi.verifyPaymentStatus(orderId);
            if (res && res.status) {
                fetchOrders();
            }
        } catch (err) {
            console.error("Error verifying payment status:", err);
        } finally {
            setRefreshingOrderId(null);
        }
    };

    const handleStartBatchVerify = async () => {
        setIsBatchVerifying(true);
        try {
            const res = await OrderApi.batchVerifyFailedToday(fromDate, toDate);
            if (res && res.status && res.response) {
                const data = res.response.data;
                setBatchResultData(data);
                setShowBatchResultModal(true);
                fetchOrders();
            }
        } catch (err) {
            console.error("Error batch verifying failed orders:", err);
        } finally {
            setIsBatchVerifying(false);
        }
    };

    const handleToggleBatchOrderSelect = (orderId) => {
        setSelectedBatchOrderIds(prev => {
            if (prev.includes(orderId)) {
                return prev.filter(id => id !== orderId);
            } else {
                return [...prev, orderId];
            }
        });
    };

    const handleSelectAllBatchOrders = () => {
        if (!batchResultData || !Array.isArray(batchResultData.verifiedPaidOrders)) return;
        if (selectedBatchOrderIds.length === batchResultData.verifiedPaidOrders.length) {
            setSelectedBatchOrderIds([]);
        } else {
            setSelectedBatchOrderIds(batchResultData.verifiedPaidOrders.map(o => o.id));
        }
    };

    const handleConfirmBatchPaid = async () => {
        if (selectedBatchOrderIds.length === 0) return;
        setIsConfirmingBatch(true);
        try {
            const res = await OrderApi.confirmBatchPaid(selectedBatchOrderIds);
            if (res && res.status) {
                setShowBatchResultModal(false);
                fetchOrders();
            }
        } catch (err) {
            console.error("Error confirming batch paid:", err);
        } finally {
            setIsConfirmingBatch(false);
        }
    };

    const fetchOrders = async () => {
        setLoading(true);
        try {
            // Priority: categoryStatus if provided, otherwise use statusFilter state
            const statusFilterValue = categoryStatus !== "" ? categoryStatus : statusFilter;
            const payStatusFilter = categoryPayStatus || paymentFilter;

            const res = await OrderApi.getOrderList(currentPage, rowsPerPage, statusFilterValue, payStatusFilter, searchTerm, "desc", "", fromDate, toDate);
            if (res.status && res.response) {
                const data = res.response.data;
                setOrders(data.data || []);
                setTotalRecords(data.total || 0);
                if (data.counts) {
                    setCounts(data.counts);
                }
                const listIds = (data.data || []).map(o => o.id || o._id);
                sessionStorage.setItem("orderIdsList", JSON.stringify(listIds));
            }
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [currentPage, rowsPerPage, searchTerm, paymentFilter, statusFilter, categoryStatus, categoryPayStatus, fromDate, toDate]);

    const handleCancelPrinting = () => {
        isPrintingCancelledRef.current = true;
        setCurrentPrintIndex(-1);
        setPrintQueue([]);
    };

    const handlePageChange = (page) => setCurrentPage(page);
    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(parseInt(e.target.value));
        setCurrentPage(0);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(0);
    };

    const exportToExcel = () => {
        const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        const fileExtension = ".xlsx";
        const fileName = `${pageTitle}_Orders_${new Date().toLocaleDateString()}`;

        const ordersToMap = selectedOrders.length > 0 ? selectedOrders : orders;

        const dataToExport = ordersToMap.map((order, index) => {
            const payObj = order?.paymentDetails || order?.paymentdetails;
            const payData = payObj?.data;
            const merchantTxnId = order?.transactionId || payData?.merchantTransactionId || "-";
            const providerTxnId = payData?.transactionId || payObj?.providerReferenceId || "-";

            return {
                "S.No": index + 1,
                "View Link": `${window.location.origin}/customer-orders/view/${order.id || order._id}`,
                "Tracking ID": order.trackingId || "-",
                "Order ID": order.orderId,
                "Date & Time": formatDateTime(order.createdAt),
                "Customer": order.address?.name || order.userName || "N/A",
                "Phone number": order.address?.phone || order.userPhone || "N/A",
                "Payment Via": order.paymentMethod || "N/A",
                "Merchant Txn ID": merchantTxnId,
                "Transaction ID": providerTxnId,
                "Payment Status": order.paymentStatus || "N/A",
                "Status": order.orderStatus === "Unfullied" ? "Unfulfilled" : (order.orderStatus || "N/A"),
                "Qty": order.totalQuantity || order.products?.reduce((acc, p) => acc + (p.qty || 0), 0) || 0,
                "Total Amount": order.grandTotal || 0
            };
        });

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: fileType });
        saveAs(data, fileName + fileExtension);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedOrders(prev => {
                const newSelection = [...prev];
                orders.forEach(order => {
                    const id = order.id || order._id;
                    if (!newSelection.some(item => (item.id || item._id) === id)) {
                        newSelection.push(order);
                    }
                });
                return newSelection;
            });
        } else {
            setSelectedOrders(prev => {
                const pageIds = orders.map(order => order.id || order._id);
                return prev.filter(item => !pageIds.includes(item.id || item._id));
            });
        }
    };

    const handleSelectItem = (order) => {
        setSelectedOrders(prev => {
            const id = order.id || order._id;
            const exists = prev.some(item => (item.id || item._id) === id);
            if (exists) {
                return prev.filter(item => (item.id || item._id) !== id);
            } else {
                return [...prev, order];
            }
        });
    };

    const handleBulkDelete = async () => {
        if (selectedOrders.length === 0) return;
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        setIsDeleting(true);
        try {
            const res = await OrderApi.deleteOrders(selectedOrders.map(o => o.id || o._id));
            if (res.status) {
                setSelectedOrders([]);
                fetchOrders();
            }
        } catch (error) {
            console.error("Error deleting orders:", error);
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    };

    const printInvoices = async () => {
        if (selectedOrders.length === 0) return;

        try {
            isPrintingCancelledRef.current = false;
            setPrintQueue([...selectedOrders]);
            setCurrentPrintIndex(0);

            // Load html2pdf.js dynamically from stable Cloudflare CDN
            await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js");

            // Loop through selected orders sequentially to generate and download PDFs silently
            for (let i = 0; i < selectedOrders.length; i++) {
                // Check if the print queue was cancelled by the user
                if (isPrintingCancelledRef.current) {
                    break;
                }

                setCurrentPrintIndex(i);
                const order = selectedOrders[i];

                // Wait 400ms to ensure React state has rendered the single active invoice in the DOM
                await new Promise((resolve) => setTimeout(resolve, 400));

                if (isPrintingCancelledRef.current) {
                    break;
                }

                const element = document.getElementById(`invoice-sheet-${order.id || order._id}`);
                if (element) {
                    const opt = {
                        margin: [5, 10],
                        filename: `Invoice_${order.orderId || order.id}.pdf`,
                        image: { type: 'jpeg', quality: 0.98 },
                        html2canvas: { scale: 2, useCORS: true, logging: false, imageTimeout: 2000, scrollY: 0, scrollX: 0 },
                        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                        pagebreak: { mode: ['css', 'legacy'] }
                    };

                    // Silent pure client-side PDF download without showing any print dialog!
                    await window.html2pdf().set(opt).from(element).save();
                }
            }
        } catch (error) {
            console.error("Error generating PDF invoices silently:", error);
        } finally {
            setCurrentPrintIndex(-1);
            setPrintQueue([]);
        }
    };

    const handleClearFilters = () => {
        setFromDate("");
        setToDate("");
        setSearchTerm("");
        setPaymentFilter("");
        setStatusFilter("");
        setCurrentPage(0);
        // fetchOrders is called automatically by useEffect due to state changes
    };

    return (
        <>
            <h6 className="text-primary-600 pb-2 mb-20">{pageTitle}</h6>
            <div className="card h-100 p-0 radius-12 overflow-hidden shadow-sm border-0">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="d-flex align-items-center gap-1">
                            <span className="fw-bold text-secondary-light" style={{ fontSize: '13px' }}>From:</span>
                            <div style={{ width: '150px' }}>
                                <StandardDatePicker
                                    name="fromDate"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="form-control form-control-sm radius-8"
                                />
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <span className="fw-bold text-secondary-light" style={{ fontSize: '13px' }}>To:</span>
                            <div style={{ width: '150px' }}>
                                <StandardDatePicker
                                    name="toDate"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="form-control form-control-sm radius-8"
                                />
                            </div>
                        </div>
                        {(fromDate || toDate || searchTerm || paymentFilter || (statusFilter !== "" && !categoryStatus)) && (
                            <button
                                onClick={handleClearFilters}
                                className="btn btn-danger-600 btn-sm radius-8 px-12 py-6 d-flex align-items-center gap-1"
                            >
                                <Icon icon="solar:close-circle-bold" />
                                Clear
                            </button>
                        )}
                        {(categoryPayStatus === "Unpaid" || pageTitle === "Failed Orders" || categoryPayStatus === "Failed") && (
                            <button
                                type="button"
                                onClick={handleStartBatchVerify}
                                disabled={isBatchVerifying}
                                className="btn btn-warning-600 btn-sm radius-8 px-14 py-6 d-flex align-items-center gap-2 fw-bold text-white shadow-sm ms-2"
                                style={{ backgroundColor: '#eab308', borderColor: '#ca8a04', height: '38px' }}
                                title="Re-test all failed orders against PhonePe payment gateway"
                            >
                                <Icon
                                    icon="ion:reload-outline"
                                    className="text-lg"
                                    style={{
                                        animation: isBatchVerifying ? "spin 1s linear infinite" : "none"
                                    }}
                                />
                                <span>{isBatchVerifying ? "Testing PhonePe Payments..." : "Re-test Failed Orders"}</span>
                            </button>
                        )}
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
                            <input
                                type="text"
                                className="bg-neutral-50 border radius-8 ps-36 pe-12 h-38-px text-sm w-170-px"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            <Icon icon="ion:search-outline" className="icon text-lg text-secondary-light position-absolute start-0 ms-12 top-50 translate-middle-y" />
                        </form>

                        {!categoryStatus && (
                            <div className="d-flex align-items-center gap-1">
                                <span className="fw-medium text-secondary-light" style={{ fontSize: '13px' }}>Order status:</span>
                                <select
                                    className="form-select form-select-sm radius-8 fw-medium"
                                    style={{ width: "125px", height: "38px", backgroundColor: "#364e3d", color: "white", borderColor: "#364e3d" }}
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(0); }}
                                >
                                    <option value="Unfulfilled">Unfulfilled</option>
                                    <option value="Dispatched">Dispatched</option>
                                    <option value="">All</option>
                                </select>
                            </div>
                        )}

                        {!categoryPayStatus && (
                            <div className="d-flex align-items-center gap-1">
                                <span className="fw-medium text-secondary-light" style={{ fontSize: '13px' }}>Payment:</span>
                                <select
                                    className="form-select form-select-sm radius-8 fw-medium"
                                    style={{ width: "120px", height: "38px", backgroundColor: "#364e3d", color: "white", borderColor: "#364e3d" }}
                                    value={paymentFilter}
                                    onChange={(e) => { setPaymentFilter(e.target.value); setCurrentPage(0); }}
                                >
                                    <option value="">All Status</option>
                                    <option value="Paid">Paid</option>
                                    {/* <option value="Unpaid">Unpaid</option> */}
                                    <option value="Pending">Pending</option>
                                    <option value="Failed">Failed</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                <div className="card-body p-24">
                    {/* Top Export Button Removed */}

                    <div className="table-responsive">
                        <table className="table bordered-table mb-0 sm-table">
                            <thead>
                                <tr>
                                    {showCheckbox && (
                                        <th className="text-white fw-bold text-md uppercase text-center px-16 py-16" style={{ width: '60px', minWidth: '60px', backgroundColor: "#364e3d", fontSize: "14px" }}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                style={{ width: '22px', height: '22px', cursor: 'pointer', flexShrink: 0 }}
                                                onChange={handleSelectAll}
                                                checked={orders.length > 0 && orders.every(order => selectedOrders.some(item => (item.id || item._id) === (order.id || order._id)))}
                                            />
                                        </th>
                                    )}
                                    <th className="text-white fw-bold text-md uppercase text-center px-16 py-16" style={{ width: '60px', backgroundColor: "#364e3d", fontSize: "14px" }}>S.No</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", fontSize: "14px" }}>View Link</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", fontSize: "14px" }}>Tracking ID</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", fontSize: "14px" }}>Order ID</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", fontSize: "14px" }}>Date & Time</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", fontSize: "14px" }}>Customer</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", fontSize: "14px" }}>Customer Phone</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", fontSize: "14px" }}>Payment Via</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", fontSize: "14px" }}>Payment Status</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", fontSize: "14px" }}>Status</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", textAlign: 'right', fontSize: "14px" }}>Qty</th>
                                    <th className="text-white fw-bold text-md uppercase px-16 py-16" style={{ backgroundColor: "#364e3d", textAlign: 'right', fontSize: "14px" }}>Total Amount</th>
                                    <th className="text-white fw-bold text-md uppercase text-center px-16 py-16" style={{ width: '120px', backgroundColor: "#364e3d", fontSize: "14px" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="10" className="text-center py-40">
                                            <div className="spinner-border text-primary-600" role="status"></div>
                                        </td>
                                    </tr>
                                ) : orders.length > 0 ? (
                                    orders.map((order, index) => (
                                        <tr key={order.id || order._id}>
                                            {showCheckbox && (
                                                <td className="text-center" style={{ width: '60px', minWidth: '60px' }}>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        style={{ width: '22px', height: '22px', cursor: 'pointer', flexShrink: 0 }}
                                                        checked={selectedOrders.some(item => (item.id || item._id) === (order.id || order._id))}
                                                        onChange={() => handleSelectItem(order)}
                                                    />
                                                </td>
                                            )}
                                            <td className="text-center">{(currentPage * rowsPerPage) + index + 1}</td>
                                            <td>
                                                <Link
                                                    to={`/customer-orders/view/${order.id || order._id}`}
                                                    state={{ sourceModule: pageTitle }}
                                                    className="d-inline-flex align-items-center gap-1 fw-semibold px-10 py-4 radius-6"
                                                    style={{ fontSize: '13px', whiteSpace: 'nowrap', backgroundColor: 'rgba(54, 78, 61, 0.1)', color: '#364e3d' }}
                                                    title={`${window.location.origin}/customer-orders/view/${order.id || order._id}`}
                                                >
                                                    <Icon icon="solar:link-bold" className="text-md" />
                                                    <span>View Link</span>
                                                </Link>
                                            </td>
                                            <td className="fw-bold text-primary-600">{order.trackingId || "-"}</td>
                                            <td className="fw-bold text-primary-600">{order.orderId}</td>
                                            <td>{formatDateTime(order.createdAt)}</td>
                                            <td>{order.address?.name || order.userName || "N/A"}</td>
                                            <td>{order.address?.phone || order.userPhone || "N/A"}</td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="fw-bold text-xs uppercase">{order.paymentMethod}</span>
                                                    {(() => {
                                                        const payObj = order?.paymentDetails || order?.paymentdetails;
                                                        const payData = payObj?.data;
                                                        const merchantTxnId = order?.transactionId || payData?.merchantTransactionId;
                                                        const providerTxnId = payData?.transactionId;

                                                        return (
                                                            <>
                                                                {merchantTxnId && (
                                                                    <span className="text-secondary-light" style={{ fontSize: '10px' }}>
                                                                        {merchantTxnId}
                                                                    </span>
                                                                )}
                                                                {providerTxnId && providerTxnId !== merchantTxnId && (
                                                                    <span className="text-secondary-light fw-semibold" style={{ fontSize: '10px', color: '#16a34a' }}>
                                                                        Txn ID: {providerTxnId}
                                                                    </span>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className={`badge radius-4 px-12 py-6 text-xs fw-bold uppercase ${order.paymentStatus === "Paid" ? "bg-success-focus text-success-main" :
                                                        order.paymentStatus === "Failed" ? "bg-danger-focus text-danger-main" :
                                                            "bg-warning-focus text-warning-main"
                                                        }`}>
                                                        {order.paymentStatus}
                                                    </span>
                                                    {order.paymentStatus !== "Paid" && (
                                                        <button
                                                            type="button"
                                                            title="Verify Payment Status with PhonePe"
                                                            className="btn btn-sm p-1 border-0 bg-transparent text-secondary-light text-hover-primary d-inline-flex align-items-center justify-content-center"
                                                            onClick={() => handleVerifyPaymentStatus(order.id || order._id)}
                                                            disabled={refreshingOrderId === (order.id || order._id)}
                                                            style={{ cursor: "pointer", minWidth: "24px", height: "24px" }}
                                                        >
                                                            <Icon
                                                                icon="ion:reload-outline"
                                                                className="text-md"
                                                                style={{
                                                                    animation: refreshingOrderId === (order.id || order._id) ? "spin 1s linear infinite" : "none"
                                                                }}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge radius-4 px-12 py-6 text-xs fw-bold uppercase ${order.orderStatus === "Dispatched" ? "bg-success-focus text-success-main" :
                                                    order.orderStatus === "Cancelled" ? "bg-danger-focus text-danger-main" :
                                                        "bg-warning-focus text-warning-main"
                                                    }`}>
                                                    {order.orderStatus === "Unfullied" ? "Unfulfilled" : order.orderStatus}
                                                </span>
                                            </td>
                                            <td className="fw-bold" style={{ textAlign: 'right' }}>
                                                {order.totalQuantity || order.products?.reduce((acc, p) => acc + (p.qty || 0), 0) || 0}
                                            </td>
                                            <td className="fw-bold" style={{ textAlign: 'right' }}>₹{order.grandTotal?.toLocaleString()}</td>
                                            <td className="text-center">
                                                <Link
                                                    className="btn btn-sm btn-primary-600 radius-8 px-12 py-6 d-inline-flex align-items-center gap-2"
                                                    style={{ fontSize: '14px' }}
                                                    to={`/customer-orders/view/${order.id || order._id}`}
                                                    state={{ sourceModule: pageTitle }}
                                                >
                                                    <Icon icon="lucide:eye" />
                                                    <span>View</span>
                                                </Link>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={showCheckbox ? 13 : 12} className="text-center py-40 text-secondary-light">No orders found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-16 d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-2">
                            {orders.length > 0 && (
                                <button
                                    onClick={exportToExcel}
                                    className="btn btn-primary-600 radius-8 px-20 d-flex align-items-center gap-2"
                                    style={{ backgroundColor: "#364e3d", borderColor: "#364e3d" }}
                                >
                                    <Icon icon="solar:file-download-bold" />
                                    Excel Export
                                </button>
                            )}
                            {showCheckbox && selectedOrders.length > 0 && (
                                <>
                                    <button
                                        onClick={printInvoices}
                                        className="btn btn-primary-600 radius-8 px-20 d-flex align-items-center gap-2"
                                        style={{ backgroundColor: "#364e3d", borderColor: "#364e3d" }}
                                    >
                                        <Icon icon="solar:bill-list-bold" />
                                        Download Invoice ({selectedOrders.length})
                                    </button>
                                    <button
                                        onClick={handleBulkDelete}
                                        disabled={isDeleting}
                                        className="btn btn-danger-600 radius-8 px-20 d-flex align-items-center gap-2"
                                    >
                                        <Icon icon="solar:trash-bin-trash-bold" />
                                        {isDeleting ? "Deleting..." : `Delete (${selectedOrders.length})`}
                                    </button>
                                </>
                            )}
                        </div>
                        <TablePagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(totalRecords / rowsPerPage)}
                            totalRecords={totalRecords}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                        />
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-primary-600">Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-24">
                    <div className="text-center">
                        <Icon icon="solar:trash-bin-trash-bold" className="text-danger-600 mb-16" style={{ fontSize: '64px' }} />
                        <h6 className="mb-8">Are you sure?</h6>
                        <p className="text-secondary-light mb-0">
                            You are about to delete <span className="fw-bold text-primary-600">{selectedOrders.length}</span> selected orders. This action cannot be undone.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer className="gap-2">
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} className="radius-8 px-24">
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={confirmDelete}
                        disabled={isDeleting}
                        className="radius-8 px-24"
                        style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                    >
                        {isDeleting ? "Deleting..." : "Yes, Delete"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Batch Verification Result Modal */}
            <Modal show={showBatchResultModal} onHide={() => setShowBatchResultModal(false)} centered size="xl">
                <Modal.Header closeButton className="bg-base border-bottom py-16 px-24">
                    <Modal.Title className="text-primary-600 fw-bold d-flex align-items-center gap-2" style={{ fontSize: '18px' }}>
                        <Icon icon="solar:restart-bold" className="text-warning-main text-xl" />
                        PhonePe Payment Gateway Re-Test Results
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-24" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    {batchResultData ? (
                        <div>
                            <p className="text-secondary-light mb-16" style={{ fontSize: '14px' }}>
                                Re-tested failed/pending orders for date: <strong>{batchResultData.dateChecked || "Today"}</strong>
                            </p>

                            {/* 5 Metrics Cards */}
                            <div className="row g-2 mb-20">
                                <div className="col" style={{ minWidth: '130px' }}>
                                    <div className="p-12 radius-8 bg-neutral-100 border text-center">
                                        <span className="text-secondary-light fw-medium d-block mb-4" style={{ fontSize: '11px' }}>Total Tested</span>
                                        <h4 className="fw-bold text-neutral-900 mb-0">{batchResultData.totalChecked || 0}</h4>
                                    </div>
                                </div>
                                <div className="col" style={{ minWidth: '130px' }}>
                                    <div className="p-12 radius-8 bg-success-focus border border-success-main text-center">
                                        <span className="text-success-main fw-bold d-block mb-4" style={{ fontSize: '11px' }}>Verified Paid (PhonePe)</span>
                                        <h4 className="fw-bold text-success-main mb-0">{batchResultData.verifiedPaidCount || 0}</h4>
                                    </div>
                                </div>
                                <div className="col" style={{ minWidth: '130px' }}>
                                    <div className="p-12 radius-8 bg-info-focus border border-info-main text-center">
                                        <span className="text-info-main fw-bold d-block mb-4" style={{ fontSize: '11px' }}>Auto Updated to Paid</span>
                                        <h4 className="fw-bold text-info-main mb-0">{batchResultData.autoUpdatedCount || 0}</h4>
                                    </div>
                                </div>
                                <div className="col" style={{ minWidth: '130px' }}>
                                    <div className="p-12 radius-8 bg-warning-focus border border-warning-main text-center">
                                        <span className="text-warning-main fw-bold d-block mb-4" style={{ fontSize: '11px' }}>Out of Stock (Failed)</span>
                                        <h4 className="fw-bold text-warning-main mb-0">{batchResultData.outOfStockCount || 0}</h4>
                                    </div>
                                </div>
                                <div className="col" style={{ minWidth: '130px' }}>
                                    <div className="p-12 radius-8 bg-danger-focus border border-danger-main text-center">
                                        <span className="text-danger-main fw-bold d-block mb-4" style={{ fontSize: '11px' }}>Remained Failed</span>
                                        <h4 className="fw-bold text-danger-main mb-0">{batchResultData.failedCount || 0}</h4>
                                    </div>
                                </div>
                            </div>

                            {/* Auto Updated Banner & Table */}
                            {batchResultData.autoUpdatedCount > 0 && (
                                <div className="mb-20">
                                    <div className="alert alert-success d-flex align-items-center gap-2 mb-16 radius-8">
                                        <Icon icon="solar:check-circle-bold" className="text-2xl flex-shrink-0" />
                                        <div>
                                            <strong>🎉 {batchResultData.autoUpdatedCount} order(s) had sufficient stock and were AUTOMATICALLY converted to Paid & stock was deducted!</strong>
                                        </div>
                                    </div>

                                    {Array.isArray(batchResultData.autoUpdatedOrders) && batchResultData.autoUpdatedOrders.length > 0 && (
                                        <div>
                                            <h6 className="fw-bold text-success-main mb-12" style={{ fontSize: '15px' }}>
                                                Auto Updated to Paid Orders (Success):
                                            </h6>
                                            <div className="table-responsive border radius-8 mb-20" style={{ maxHeight: '360px', overflowY: 'auto' }}>
                                                <table className="table bordered-table mb-0 sm-table">
                                                    <thead>
                                                        <tr>
                                                            <th className="bg-neutral-100 fw-bold px-12 py-10" style={{ fontSize: '13px' }}>#</th>
                                                            <th className="bg-neutral-100 fw-bold px-12 py-10" style={{ fontSize: '13px' }}>Order ID</th>
                                                            <th className="bg-neutral-100 fw-bold px-12 py-10" style={{ fontSize: '13px' }}>Customer & Mobile</th>
                                                            <th className="bg-neutral-100 fw-bold px-12 py-10" style={{ fontSize: '13px' }}>Product & Variant</th>
                                                            <th className="bg-neutral-100 fw-bold px-12 py-10 text-end" style={{ fontSize: '13px' }}>Amount</th>
                                                            <th className="bg-neutral-100 fw-bold px-12 py-10 text-center" style={{ fontSize: '13px' }}>Order Qty</th>
                                                            <th className="bg-neutral-100 fw-bold px-12 py-10 text-center" style={{ fontSize: '13px' }}>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {batchResultData.autoUpdatedOrders.map((ord, idx) => (
                                                            <tr key={ord.id || idx}>
                                                                <td className="px-12 py-10 text-center align-middle">{idx + 1}</td>
                                                                <td className="px-12 py-10 fw-bold text-success-main align-middle" style={{ whiteSpace: 'nowrap' }}>
                                                                    {ord.orderId}
                                                                </td>
                                                                <td className="px-12 py-10 align-middle" style={{ whiteSpace: 'nowrap' }}>
                                                                    <span className="fw-medium text-neutral-900 d-block">{ord.customerName}</span>
                                                                    <span className="text-xs text-neutral-500">{ord.phone}</span>
                                                                </td>
                                                                <td className="px-12 py-10 align-middle">
                                                                    {Array.isArray(ord.products) && ord.products.map((p, pIdx) => (
                                                                        <div key={pIdx} className={pIdx > 0 ? "mt-4 pt-4 border-top" : ""}>
                                                                            <span className="fw-bold text-neutral-900">{p.productName}</span>
                                                                            <span className="text-xs text-neutral-500 d-block">{p.variantName} <span className="text-neutral-400">({p.sku})</span></span>
                                                                        </div>
                                                                    ))}
                                                                </td>
                                                                <td className="px-12 py-10 text-end fw-bold text-neutral-900 align-middle" style={{ whiteSpace: 'nowrap' }}>
                                                                    ₹{ord.amount?.toLocaleString()}
                                                                </td>
                                                                <td className="px-12 py-10 text-center fw-bold align-middle">
                                                                    {Array.isArray(ord.products) && ord.products.map((p, pIdx) => (
                                                                        <div key={pIdx} className={pIdx > 0 ? "mt-4 pt-4 border-top" : ""}>
                                                                            {p.orderQty}
                                                                        </div>
                                                                    ))}
                                                                </td>
                                                                <td className="px-12 py-10 text-center align-middle">
                                                                    <span className="badge bg-success-focus text-success-main radius-4 px-8 py-4 fw-bold" style={{ fontSize: '11px' }}>
                                                                        AUTO PAID
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Out of Stock Notice & Table */}
                            {batchResultData.outOfStockCount > 0 && (
                                <div>
                                    <div className="alert alert-warning d-flex align-items-center gap-2 mb-16 radius-8 border border-warning-main" style={{ backgroundColor: '#fffbeb' }}>
                                        <Icon icon="solar:danger-bold" className="text-2xl text-warning-main flex-shrink-0" />
                                        <div>
                                            <strong className="text-warning-main" style={{ fontSize: '14px' }}>
                                                {batchResultData.outOfStockCount} order(s) verified as Paid on PhonePe, but products are OUT OF STOCK!
                                            </strong>
                                            <span className="d-block text-xs text-secondary-light mt-1">
                                                Please click on the product name links below to update product stock. Re-test again after updating stock to process them.
                                            </span>
                                        </div>
                                    </div>

                                    <h6 className="fw-bold text-neutral-900 mb-12" style={{ fontSize: '15px' }}>
                                        Out of Stock Orders (Action Required):
                                    </h6>

                                    <div className="table-responsive border radius-8 mb-20" style={{ maxHeight: '360px', overflowY: 'auto' }}>
                                        <table className="table bordered-table mb-0 sm-table">
                                            <thead>
                                                <tr>
                                                    <th className="bg-neutral-100 fw-bold px-12 py-10" style={{ fontSize: '13px' }}>#</th>
                                                    <th className="bg-neutral-100 fw-bold px-12 py-10" style={{ fontSize: '13px' }}>Order ID</th>
                                                    <th className="bg-neutral-100 fw-bold px-12 py-10" style={{ fontSize: '13px' }}>Customer & Mobile</th>
                                                    <th className="bg-neutral-100 fw-bold px-12 py-10" style={{ fontSize: '13px' }}>Product & Variant</th>
                                                    <th className="bg-neutral-100 fw-bold px-12 py-10 text-end" style={{ fontSize: '13px' }}>Amount</th>
                                                    <th className="bg-neutral-100 fw-bold px-12 py-10 text-center" style={{ fontSize: '13px' }}>Order Qty</th>
                                                    <th className="bg-neutral-100 fw-bold px-12 py-10 text-center" style={{ fontSize: '13px' }}>Current Stock</th>
                                                    <th className="bg-neutral-100 fw-bold px-12 py-10 text-center" style={{ fontSize: '13px' }}>Stock Condition</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {batchResultData.outOfStockOrders.map((ord, idx) => (
                                                    <tr key={ord.id || idx}>
                                                        <td className="px-12 py-10 text-center align-middle">{idx + 1}</td>
                                                        <td className="px-12 py-10 fw-bold text-primary-600 align-middle" style={{ whiteSpace: 'nowrap' }}>
                                                            {ord.orderId}
                                                        </td>
                                                        <td className="px-12 py-10 align-middle" style={{ whiteSpace: 'nowrap' }}>
                                                            <span className="fw-medium text-neutral-900 d-block">{ord.customerName}</span>
                                                            <span className="text-xs text-neutral-500">{ord.phone}</span>
                                                        </td>
                                                        <td className="px-12 py-10 align-middle">
                                                            {Array.isArray(ord.products) && ord.products.map((p, pIdx) => (
                                                                <div key={pIdx} className={pIdx > 0 ? "mt-4 pt-4 border-top" : ""}>
                                                                    {p.productId ? (
                                                                        <Link
                                                                            to={`/products/stock-update/${p.productId}`}
                                                                            target="_blank"
                                                                            className="fw-bold text-primary-600 hover-text-primary text-decoration-underline d-inline-flex align-items-center gap-1"
                                                                            title="Click to open stock update page in new tab"
                                                                        >
                                                                            {p.productName}
                                                                            <Icon icon="solar:export-bold" style={{ fontSize: '12px' }} />
                                                                        </Link>
                                                                    ) : (
                                                                        <span className="fw-bold text-neutral-900">{p.productName}</span>
                                                                    )}
                                                                    <span className="text-xs text-neutral-500 d-block">{p.variantName} <span className="text-neutral-400">({p.sku})</span></span>
                                                                </div>
                                                            ))}
                                                        </td>
                                                        <td className="px-12 py-10 text-end fw-bold text-neutral-900 align-middle" style={{ whiteSpace: 'nowrap' }}>
                                                            ₹{ord.amount?.toLocaleString()}
                                                        </td>
                                                        <td className="px-12 py-10 text-center fw-bold align-middle">
                                                            {Array.isArray(ord.products) && ord.products.map((p, pIdx) => (
                                                                <div key={pIdx} className={pIdx > 0 ? "mt-4 pt-4 border-top" : ""}>
                                                                    {p.orderQty}
                                                                </div>
                                                            ))}
                                                        </td>
                                                        <td className="px-12 py-10 text-center fw-bold align-middle">
                                                            {Array.isArray(ord.products) && ord.products.map((p, pIdx) => (
                                                                <div key={pIdx} className={pIdx > 0 ? "mt-4 pt-4 border-top" : ""}>
                                                                    <span className={p.isOutOfStock ? "text-danger-main" : "text-success-main"}>
                                                                        {p.currentStock}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </td>
                                                        <td className="px-12 py-10 text-center align-middle">
                                                            <span className="badge bg-danger-focus text-danger-main radius-4 px-8 py-4 fw-bold" style={{ fontSize: '11px' }}>
                                                                OUT OF STOCK
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* No changes banner */}
                            {batchResultData.autoUpdatedCount === 0 && batchResultData.outOfStockCount === 0 && (
                                <div className="alert alert-neutral d-flex align-items-center gap-2 mb-0 radius-8 border" style={{ backgroundColor: '#f8fafc' }}>
                                    <Icon icon="solar:info-circle-bold" className="text-xl text-secondary-light flex-shrink-0" />
                                    <div>
                                        <strong className="text-secondary-light">No order payment status changed.</strong>
                                        <span className="d-block text-xs text-secondary-light mt-1">All {batchResultData.totalChecked} tested orders are confirmed as failed on PhonePe.</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-30">
                            <div className="spinner-border text-primary-600" role="status"></div>
                            <p className="mt-12 text-secondary-light">Querying PhonePe Payment Gateway status & checking stock...</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-base border-top py-12 px-24 d-flex align-items-center justify-content-end">
                    <Button variant="secondary" className="radius-8 px-24 fw-medium" onClick={() => setShowBatchResultModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Hidden Print Invoices Section */}
            <div className="print-invoices-container">
                <style>{`
                    .print-invoices-container {
                        position: absolute;
                        left: -9999px;
                        top: -9999px;
                        width: 800px;
                        background: #fff;
                    }
                    @media print {
                        /* Hide navigation, headers, footers and screen cards */
                        aside.sidebar,
                        header,
                        .navbar-header,
                        .navbar,
                        .d-footer,
                        footer,
                        .no-print,
                        .card,
                        h6.text-primary-600,
                        .navbar-search {
                            display: none !important;
                        }

                        /* Reset layout boundaries and backgrounds for printing */
                        body, html {
                            background: #fff !important;
                            color: #000 !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            width: 100% !important;
                            height: auto !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        
                        .dashboard-main {
                            margin-left: 0 !important;
                            padding: 0 !important;
                            width: 100% !important;
                            min-width: 100% !important;
                            background: #fff !important;
                        }

                        .dashboard-main-body {
                            padding: 0 !important;
                            margin: 0 !important;
                            background: #fff !important;
                        }

                        .print-invoices-container {
                            display: block !important;
                            width: 100% !important;
                            margin: 0 !important;
                            padding: 0 !important;
                        }
                    }

                    /* Tax Invoice Page Layout - Active for both PDF compilation & Native Print */
                    .print-invoice-paper {
                        background: #fff !important;
                        border: none !important;
                        box-shadow: none !important;
                        padding: 20px !important;
                        margin: 0 !important;
                        width: 100% !important;
                        box-sizing: border-box !important;
                        font-family: 'Inter', sans-serif !important;
                        color: #333 !important;
                        page-break-after: always !important;
                        break-after: page !important;
                    }

                    .print-invoice-paper:last-child {
                        page-break-after: avoid !important;
                        break-after: avoid !important;
                    }

                    .tax-invoice-label {
                        background: #fdfaf9 !important;
                        text-align: center !important;
                        padding: 0.5rem !important;
                        font-weight: 700 !important;
                        font-size: 12px !important;
                        letter-spacing: 0.1em !important;
                        border-bottom: 1px solid #eee !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .invoice-header-grid {
                        display: grid !important;
                        grid-template-columns: 1.2fr 1fr 1fr !important;
                        border: 1px solid #ccc !important;
                    }
                    .header-full-row {
                        grid-column: 1 / -1 !important;
                        padding: 0.5rem !important;
                        text-align: center !important;
                        font-size: 14px !important;
                        font-weight: 800 !important;
                        color: #333 !important;
                        border-bottom: 1px solid #ccc !important;
                        background: #fff !important;
                        text-transform: uppercase !important;
                    }
                    .branding-cell {
                        grid-row: span 3 !important;
                        padding: 1.5rem !important;
                        border-right: 1px solid #ccc !important;
                        text-align: left !important;
                        display: flex !important;
                        flex-direction: column !important;
                        gap: 1rem !important;
                    }
                    .invoice-logo-box {
                        width: 100px !important;
                        height: 100px !important;
                        background: #36533f !important;
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        border-radius: 4px !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .invoice-logo-box img {
                        width: 80% !important;
                        height: auto !important;
                    }
                    .company-name {
                        font-size: 28px !important;
                        font-weight: 900 !important;
                        color: #333 !important;
                        margin-bottom: 4px !important;
                    }
                    .company-gst {
                        font-size: 14px !important;
                        font-weight: 800 !important;
                        margin-bottom: 1rem !important;
                    }
                    .company-meta {
                        font-size: 12px !important;
                        color: #555 !important;
                        line-height: 1.6 !important;
                    }
                    .grid-cell {
                        padding: 1rem !important;
                        border-bottom: 1px solid #ccc !important;
                        border-right: 1px solid #ccc !important;
                    }
                    .grid-cell:nth-child(3n) {
                        border-right: none !important;
                    }
                    .grid-cell.no-border-bottom {
                        border-bottom: none !important;
                    }
                    .cell-label {
                        font-size: 12px !important;
                        font-weight: 800 !important;
                        color: #333 !important;
                        margin-bottom: 4px !important;
                        display: block !important;
                    }
                    .cell-content {
                        font-size: 13px !important;
                        color: #444 !important;
                    }
                    .addr-title {
                        font-size: 13px !important;
                        font-weight: 800 !important;
                        color: #333 !important;
                        margin-bottom: 6px !important;
                        display: block !important;
                    }
                    .addr-text {
                        font-size: 12px !important;
                        line-height: 1.5 !important;
                        color: #555 !important;
                    }
                    .items-table {
                        width: 100% !important;
                        border-collapse: collapse !important;
                    }
                    .items-table th {
                        background: #f8f9fa !important;
                        color: #333 !important;
                        border: 1px solid #ddd !important;
                        padding: 0.75rem !important;
                        font-size: 12px !important;
                        font-weight: 800 !important;
                        text-align: center !important;
                        text-transform: uppercase !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .items-table td {
                        padding: 0.75rem !important;
                        border: 1px solid #ddd !important;
                        vertical-align: middle !important;
                        font-size: 13px !important;
                    }
                    .amount-summary-row {
                        display: flex !important;
                        justify-content: space-between !important;
                        align-items: center !important;
                        padding: 0.75rem 2rem !important;
                        background: #fff !important;
                        border-top: 1px solid #ddd !important;
                        border-bottom: 1px solid #ddd !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    .amount-summary-row .words-box {
                        font-size: 12px !important;
                        color: #666 !important;
                    }
                    .words-val {
                        font-weight: 700 !important;
                        color: #333 !important;
                        text-transform: capitalize !important;
                    }
                    .amount-summary-row .pay-box {
                        font-size: 15px !important;
                        font-weight: 700 !important;
                        color: #333 !important;
                    }
                    .amt-val {
                        font-size: 20px !important;
                        color: #36533f !important;
                        margin-left: 0.5rem !important;
                    }
                    .invoice-footer {
                        display: grid !important;
                        grid-template-columns: 1fr 1fr !important;
                        border-bottom: 1px solid #eee !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    .footer-left {
                        padding: 1rem 2rem !important;
                        border-right: 1px solid #eee !important;
                    }
                    .footer-right {
                        padding: 1rem 2rem !important;
                        display: flex !important;
                        justify-content: flex-end !important;
                        align-items: center !important;
                    }
                    .signatory-box {
                        text-align: center !important;
                        display: flex !important;
                        flex-direction: column !important;
                        align-items: center !important;
                        gap: 0.5rem !important;
                    }
                    .signatory-label {
                        font-size: 14px !important;
                        font-weight: 800 !important;
                        color: #333 !important;
                    }
                    .terms-section {
                        padding: 1rem 2rem !important;
                        background: #fff !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    .terms-title {
                        font-size: 13px !important;
                        font-weight: bold !important;
                    }
                    .terms-section ul {
                        padding-left: 1.2rem !important;
                    }
                    .terms-section li {
                        font-size: 12px !important;
                        color: #444 !important;
                        margin-bottom: 0.5rem !important;
                        line-height: 1.5 !important;
                    }
                `}</style>
                {printQueue.length > 0 && (
                    <div className="no-print position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999 }}>
                        <div className="bg-white p-32 radius-12 shadow-lg text-center" style={{ maxWidth: '400px', border: '1px solid #eee' }}>
                            <div className="spinner-border text-primary-600 mb-16" role="status" style={{ width: '3rem', height: '3rem', color: '#364e3d' }}></div>
                            <h6 className="mb-8 fw-bold" style={{ color: '#364e3d' }}>Downloading Invoices</h6>
                            <p className="text-secondary-light text-sm mb-24">
                                Processing and saving invoice <span className="fw-bold text-primary-600" style={{ color: '#364e3d' }}>{currentPrintIndex + 1}</span> of <span className="fw-bold text-primary-600" style={{ color: '#364e3d' }}>{printQueue.length}</span>...
                            </p>
                            <button
                                onClick={handleCancelPrinting}
                                className="btn btn-danger-600 radius-8 px-24 fw-bold w-100"
                                style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                            >
                                Cancel Remaining
                            </button>
                        </div>
                    </div>
                )}

                {currentPrintIndex !== -1 && printQueue[currentPrintIndex] ? (
                    (() => {
                        const order = printQueue[currentPrintIndex];
                        const cgst = (order.taxAmount || 0) / 2;
                        const sgst = (order.taxAmount || 0) / 2;
                        const subtotal = order.totalAmount || (order.grandTotal - (order.taxAmount || 0) - (order.shippingCharge || 0) + (order.overallDiscount || 0));

                        return (
                            <div key={order.id || order._id} id={`invoice-sheet-${order.id || order._id}`} className="print-invoice-paper">
                                <div className="tax-invoice-label">TAX INVOICE</div>
                                <div className="invoice-header-grid">
                                    <div className="header-full-row">Tax Invoice</div>

                                    {/* Branding */}
                                    <div className="branding-cell">
                                        <div className="invoice-logo-box">
                                            <img src="/logo.jpg" alt="Logo" />
                                        </div>
                                        <div className="branding-info">
                                            <div className="company-name">Prrayasha Collections</div>
                                            <div className="company-gst">GSTN : 33AWPPJ9059B2ZD</div>
                                            <div className="company-meta">
                                                Provident Cosmo City, DR Abdul Kalam Road,<br />
                                                Pudhupakkam Village, Chengalpet 603103<br />
                                                +91 91590 24967
                                            </div>
                                        </div>
                                    </div>

                                    {/* Row 1: Invoice Details */}
                                    <div className="grid-cell">
                                        <div className="cell-content">
                                            <strong>Invoice # {order.orderId || (order.id || order._id)?.toString().slice(-6).toUpperCase()}</strong>
                                        </div>
                                        <div className="cell-content" style={{ marginTop: '8px' }}>
                                            Invoice Date: <strong>{order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</strong>
                                        </div>
                                    </div>
                                    <div className="grid-cell">
                                        <div className="cell-content">
                                            Payment Status: <span className="status-val">{order.paymentStatus}</span>
                                        </div>
                                        <div className="cell-content" style={{ marginTop: '8px' }}>
                                            Payment Method: <strong>{order.paymentMethod}</strong>
                                        </div>
                                        {(() => {
                                            const payObj = order?.paymentDetails || order?.paymentdetails;
                                            const payData = payObj?.data;
                                            const merchantTxnId = order?.transactionId || payData?.merchantTransactionId;
                                            const providerTxnId = payData?.transactionId;

                                            return (
                                                <>
                                                    {merchantTxnId && (
                                                        <div className="cell-content" style={{ marginTop: '8px' }}>
                                                            Merchant Txn ID: <strong>{merchantTxnId}</strong>
                                                        </div>
                                                    )}
                                                    {providerTxnId && providerTxnId !== merchantTxnId && (
                                                        <div className="cell-content" style={{ marginTop: '4px' }}>
                                                            Transaction ID: <strong>{providerTxnId}</strong>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>

                                    {/* Row 2: Addresses */}
                                    <div className="grid-cell">
                                        <div className="addr-title">Billing Address:</div>
                                        <div className="addr-text">
                                            Name: {order.address?.name}<br />
                                            Phone: {order.address?.phone}<br />
                                            Address: {order.address?.doorNo}, {order.address?.street}<br />
                                            State: {order.address?.state}<br />
                                            City: {order.address?.city}<br />
                                            Zip: {order.address?.pincode}
                                        </div>
                                    </div>
                                    <div className="grid-cell">
                                        <div className="addr-title">Shipping Address:</div>
                                        <div className="addr-text">
                                            Name: {order.address?.name}<br />
                                            Phone: {order.address?.phone}<br />
                                            Address: {order.address?.doorNo}, {order.address?.street}<br />
                                            State: {order.address?.state}<br />
                                            City: {order.address?.city}<br />
                                            Zip: {order.address?.pincode}
                                        </div>
                                    </div>

                                    {/* Row 3: Supply & Due */}
                                    <div className="grid-cell no-border-bottom">
                                        <div className="cell-label">Place of Supply</div>
                                        <div className="cell-content"><strong>89 - Chengalpattu</strong></div>
                                    </div>
                                    <div className="grid-cell no-border-bottom">
                                        <div className="cell-label">Due Date:</div>
                                        <div className="cell-content"><strong>Immediate on Receipt</strong></div>
                                    </div>
                                </div>

                                {/* Items Table */}
                                <table className="items-table" style={{ marginTop: '10px' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40px' }}>#</th>
                                            <th style={{ textAlign: 'left', width: '200px' }}>Item</th>
                                            <th style={{ width: '80px' }}>Photo</th>
                                            <th style={{ width: '100px' }}>Size</th>
                                            <th style={{ width: '100px' }}>HSN/SAC</th>
                                            <th style={{ width: '80px' }}>Quantity</th>
                                            <th style={{ textAlign: 'right', width: '100px' }}>Rate/Item</th>
                                            <th style={{ textAlign: 'right', width: '100px' }}>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.products?.map((item, idx) => (
                                            <tr key={idx}>
                                                <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                <td style={{ textAlign: 'left' }}>
                                                    <div className="item-name">{item.productName}</div>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {item.image?.path ? (
                                                        <img
                                                            src={`${IMAGE_BASE_URL}/${item.image.path}`}
                                                            alt={item.productName}
                                                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #eee' }}
                                                        />
                                                    ) : (
                                                        <span style={{ color: '#ccc' }}>No Img</span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {item.combination?.map(c => c.valueName || c.value).join(', ') || '-'}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>{item.hsnCode || '-'}</td>
                                                <td style={{ textAlign: 'center' }}>{item.qty} Nos</td>
                                                <td style={{ textAlign: 'right' }}>₹{item.price?.toFixed(2)}</td>
                                                <td style={{ textAlign: 'right' }}>₹{item.total?.toFixed(2)}</td>
                                            </tr>
                                        ))}

                                        <tr className="summary-row border-top">
                                            <td colSpan="7" style={{ textAlign: 'right' }}>Total Amount - Inc. all vat/tax</td>
                                            <td style={{ textAlign: 'right' }}>₹{subtotal.toFixed(2)}</td>
                                        </tr>
                                        <tr className="summary-row">
                                            <td colSpan="7" style={{ textAlign: 'right' }}>CGST 2.5%</td>
                                            <td style={{ textAlign: 'right' }}>₹{cgst.toFixed(2)}</td>
                                        </tr>
                                        <tr className="summary-row">
                                            <td colSpan="7" style={{ textAlign: 'right' }}>SGST 2.5%</td>
                                            <td style={{ textAlign: 'right' }}>₹{sgst.toFixed(2)}</td>
                                        </tr>
                                        <tr className="summary-row">
                                            <td colSpan="7" style={{ textAlign: 'right' }}>Shipping Amount</td>
                                            <td style={{ textAlign: 'right' }}>₹{order.shippingCharge?.toFixed(2) || '0.00'}</td>
                                        </tr>
                                        <tr className="summary-row">
                                            <td colSpan="7" style={{ textAlign: 'right' }}>Discount Amount</td>
                                            <td style={{ textAlign: 'right' }}>-₹{order.overallDiscount?.toFixed(2) || '0.00'}</td>
                                        </tr>
                                        <tr className="summary-row">
                                            <td colSpan="7" style={{ textAlign: 'right' }}>Coupon Applied</td>
                                            <td style={{ textAlign: 'right' }}>-₹{order.couponDiscount?.toFixed(2) || '0.00'}</td>
                                        </tr>
                                        <tr className="summary-row grand-total-row">
                                            <td colSpan="7" style={{ textAlign: 'right' }}>Total</td>
                                            <td style={{ textAlign: 'right' }}>₹{order.grandTotal?.toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className="amount-summary-row" style={{ marginTop: '10px' }}>
                                    <div className="words-box">
                                        <span>Amount Chargeable (in words): </span>
                                        <span className="words-val"> {numberToWords(order.grandTotal)}</span>
                                    </div>
                                    <div className="pay-box">
                                        <span>Amount Payable: </span>
                                        <span className="amt-val" style={{ fontWeight: 'bold' }}>₹{order.grandTotal?.toLocaleString()}</span>
                                    </div>
                                </div>

                                <div className="invoice-footer" style={{ marginTop: '10px' }}>
                                    <div className="footer-left">
                                        <div className="notes-section">
                                            <div className="notes-title">Notes:</div>
                                            <p>Thank you for the Shopping</p>
                                        </div>
                                    </div>
                                    <div className="footer-right">
                                        <div className="signatory-box">
                                            <div className="signatory-label">For Prrayasha Collection</div>
                                            <div className="signature-area" style={{ marginTop: '5px' }}>
                                                <img src="/signature.png" alt="Signature" style={{ maxWidth: '120px' }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="terms-section" style={{ marginTop: '10px' }}>
                                    <div className="terms-title">Terms and Conditions:</div>
                                    <ul>
                                        <li>Products once sold cannot be returned or exchanged unless damage issue.</li>
                                        <li>To return or exchange for damage reasons, parcel opening video without any edits and cuts is must and has to shared with us through email (prrayashacollections@gmail.com) or whatsapp (91590 24967) with your order details.</li>
                                        <li>No return or exchange for size issues.</li>
                                        <li>For any queries or disputes related to products or payments: Email prrayashacollections@gmail.com (response within 2 working days).</li>
                                        <li>If no response, contact WhatsApp 91590 24967 (response within 2 working days).</li>
                                        <li>If unresolved thereafter, you may proceed with legal action as per applicable laws.</li>
                                    </ul>
                                </div>
                            </div>
                        );
                    })()
                ) : null}
            </div>
        </>
    );
};

export default OrderListCommonLayer;
